import { Billboard } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { useRef } from 'react';
import * as THREE from 'three';
import { activeSnapAtom } from '@/atoms/snapping.atom';
import type { SnapKind } from '@/lib/snapping/types';

// On-screen radius of the coloured ring, in pixels. The dark halo is sized
// relative to this.
const TARGET_PX = 16;

const _tmpWorld = new THREE.Vector3();

const COLOR: Record<Exclude<SnapKind, 'none'>, string> = {
  vertex: '#22d3ee',
  midpoint: '#a3e635',
  edge: '#f59e0b',
  surface: '#94a3b8',
};

// thetaSegments per kind — a square outline for vertices, triangle for
// midpoints, smooth circle for edges. Kept distinct so the *kind* of snap is
// readable at a glance.
const SEGMENTS: Record<Exclude<SnapKind, 'none' | 'surface'>, number> = {
  vertex: 4,
  midpoint: 3,
  edge: 48,
};

/**
 * Screen-space-sized glyph marking the active snap.
 *
 * Wrapped in a `<Billboard>` so it always faces the camera — a flat world-XY
 * ring foreshortens to an invisible sliver in any tilted/perspective view,
 * which was the root cause of "snapping works but I can't see it". Sized via
 * `unitsPerPixel` so it stays a constant on-screen size at any zoom (CLAUDE.md
 * "Screen-space sized handles & markers" rule), with a thin dark contrast halo
 * so it stays legible against busy point clouds without being loud.
 */
export const SnapIndicator = () => {
  const snap = useAtomValue(activeSnapAtom);
  // Scale is applied to this inner group; the parent <Billboard> owns the
  // camera-facing orientation, so the two don't fight over the transform.
  const group = useRef<THREE.Group>(null);

  useFrame(({ camera, size }) => {
    const g = group.current;
    if (!g || !snap || snap.kind === 'none' || snap.kind === 'surface') return;

    let unitsPerPixel: number;
    if (camera.type === 'OrthographicCamera') {
      const o = camera as THREE.OrthographicCamera;
      const worldScreenHeight = Math.abs(o.top - o.bottom) / Math.max(o.zoom, 1e-6);
      unitsPerPixel = worldScreenHeight / size.height;
    } else {
      _tmpWorld.set(snap.position[0], snap.position[1], snap.position[2]);
      const dist = camera.position.distanceTo(_tmpWorld);
      const p = camera as THREE.PerspectiveCamera;
      const vFov = (p.getEffectiveFOV?.() ?? p.fov) * (Math.PI / 180);
      const worldScreenHeight = 2 * dist * Math.tan(vFov / 2);
      unitsPerPixel = worldScreenHeight / size.height;
    }

    g.scale.setScalar(Math.max(TARGET_PX * unitsPerPixel, 1e-4));
  });

  if (!snap || snap.kind === 'none' || snap.kind === 'surface') return null;
  const color = COLOR[snap.kind];
  const segments = SEGMENTS[snap.kind];

  return (
    <Billboard position={snap.position} renderOrder={9999}>
      <group ref={group}>
        {/* Thin dark contrast halo behind the coloured ring so the glyph stays
            legible on bright point clouds / sky alike. */}
        <mesh renderOrder={9998}>
          <ringGeometry args={[0.42, 0.6, segments]} />
          <meshBasicMaterial color='#0b0f14' depthTest={false} transparent opacity={0.7} toneMapped={false} />
        </mesh>

        {/* Coloured ring — the primary glyph, keyed by snap kind. */}
        <mesh renderOrder={9999}>
          <ringGeometry args={[0.46, 0.56, segments]} />
          <meshBasicMaterial color={color} depthTest={false} transparent toneMapped={false} />
        </mesh>
      </group>
    </Billboard>
  );
};
